import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";

type ContactSubmission = {
  _id: string;
  name: string;
  email: string;
  phone_country_code?: string;
  phone?: string;
  message_type?: string;
  message?: string;
  status?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
};

const show = (v: unknown) =>
  v === undefined || v === null || v === "" ? "---" : String(v);

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "responded", label: "Responded" },
  { value: "closed", label: "Closed" },
];

const MESSAGE_TYPE_OPTIONS = [
  "General Inquiry",
  "Support Request",
  "Partnership",
  "Vendor Partnership",
  "Sales Inquiry",
];

export default function SuperAdminContactRequestViewEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken");
  const isEditMode = location.pathname.endsWith("/edit");

  const [data, setData] = useState<ContactSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit form state (only used in edit mode)
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editMessageType, setEditMessageType] = useState("");

  useEffect(() => {
    let aborted = false;
    async function fetchOne() {
      if (!id || !token) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/contact-submissions/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        let json: any = {};
        try {
          const text = await res.text();
          if (text) json = JSON.parse(text);
        } catch {
          // ignore
        }
        if (aborted) return;
        if (!res.ok) {
          showError(json?.message || json?.error || "Failed to load submission");
          setData(null);
          return;
        }
        const payload = json?.data ?? json;
        const submission: ContactSubmission = {
          _id: payload?._id ?? id,
          name: payload?.name ?? "",
          email: payload?.email ?? "",
          phone_country_code: payload?.phone_country_code,
          phone: payload?.phone,
          message_type: payload?.message_type,
          message: payload?.message,
          status: payload?.status ?? "pending",
          notes: payload?.notes ?? "",
          created_at: payload?.created_at,
          updated_at: payload?.updated_at,
        };
        setData(submission);
        setEditStatus(submission.status ?? "pending");
        setEditNotes(submission.notes ?? "");
        setEditMessageType(submission.message_type ?? "");
      } catch (e) {
        if (!aborted) showError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!aborted) setLoading(false);
      }
    }
    fetchOne();
    return () => { aborted = true; };
  }, [id, token]);

  const handleUpdate = async () => {
    if (!id || !token) return;
    setSaving(true);
    try {
      const body: Record<string, string> = {};
      if (editStatus !== data?.status) body.status = editStatus;
      if (editNotes !== (data?.notes ?? "")) body.notes = editNotes;
      if (editMessageType !== (data?.message_type ?? "")) body.message_type = editMessageType;
      if (Object.keys(body).length === 0) {
        showError("No changes to save");
        setSaving(false);
        return;
      }
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/contact-submissions/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      let json: any = {};
      try {
        const text = await res.text();
        if (text) json = JSON.parse(text);
      } catch {
        // ignore
      }
      if (!res.ok) {
        showError(json?.message || json?.error || "Update failed");
        return;
      }
      const updated = json?.data ?? json;
      if (updated) {
        setData((prev) => (prev ? { ...prev, ...updated } : prev));
      }
      showSuccess("Contact submission updated successfully!");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSaving(false);
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="box-shadow br-8 col-lg-7 container content mb-4 p-lg-4 br-gray">
        <div className="d-flex align-items-center justify-content-center py-5">
          Loading...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="box-shadow br-8 col-lg-7 container content mb-4 p-lg-4 br-gray">
        <div className="d-flex gap-3 justify-content-start mb-4">
          <button
            type="button"
            onClick={() => navigate("/superadmin/contact-requests")}
            className="btn back-btn surface-btn fw-bold fs-16"
          >
            Back
          </button>
        </div>
        <p className="text-muted">Submission not found.</p>
      </div>
    );
  }

  const phoneDisplay = [data.phone_country_code, data.phone].filter(Boolean).join(" ") || "---";

  return (
    <div className="box-shadow br-8 col-lg-7 container content mb-4 p-lg-4 br-gray">
      {/* Header: name + badges (profile-style) */}
      <div className="align-items-center d-flex flex-column mb-4 pt-4 pt-lg-0 text-center">
        <h3 className="mb-1">{show(data.name)}</h3>
        <p className="fs-19 mb-0 text-black-50">{show(data.email)}</p>
        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap justify-content-center">
          <span className="badge p-2 br-8" style={{ background: "#dbeafe", color: "#155dfc" }}>
            {show(data.message_type)}
          </span>
          <span className="badge p-2 br-8 bg-body-tertiary text-secondary">
            {show(data.status)}
          </span>
        </div>
      </div>

      {/* Section: Contact Information */}
      <section className="card bg-white shadow-lg br-8 mb-4" style={{ marginBottom: "22px" }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="badge p-2 br-8" style={{ background: "#dbeafe" }}>
              <i className="fs-19 fw-light ri-user-3-line" style={{ color: "#155dfc" }} />
            </span>
            <p className="fs-18 m-0">Contact Information</p>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-3">
              <label className="form-label">Name</label>
              <div className="form-control br-8 bg-light border d-flex align-items-center" style={{ height: 40 }}>
                {show(data.name)}
              </div>
            </div>
            <div className="col-lg-6 mb-3">
              <label className="form-label">Email</label>
              <div className="form-control br-8 bg-light border d-flex align-items-center" style={{ height: 40 }}>
                {show(data.email)}
              </div>
            </div>
            <div className="col-lg-6 mb-3">
              <label className="form-label">Phone</label>
              <div className="form-control br-8 bg-light border d-flex align-items-center" style={{ height: 40 }}>
                {show(phoneDisplay)}
              </div>
            </div>
            <div className="col-lg-6 mb-3">
              <label className="form-label">Message Type</label>
              {isEditMode ? (
                <select
                  className="form-control br-8"
                  value={editMessageType}
                  onChange={(e) => setEditMessageType(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {MESSAGE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <div className="form-control br-8 bg-light border d-flex align-items-center" style={{ height: 40 }}>
                  {show(data.message_type)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Message & Status */}
      <section className="card bg-white shadow-lg br-8 mb-4" style={{ marginBottom: "22px" }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="badge p-2 br-8" style={{ background: "#dbeafe" }}>
              <i className="fs-19 fw-light ri-mail-line" style={{ color: "#155dfc" }} />
            </span>
            <p className="fs-18 m-0">Message &amp; Status</p>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">Message</label>
              <div
                className="form-control br-8 bg-light border d-flex align-items-center"
                style={{ minHeight: 80, whiteSpace: "pre-wrap" }}
              >
                {show(data.message)}
              </div>
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Notes</label>
              {isEditMode ? (
                <textarea
                  className="form-control br-8"
                  rows={4}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes..."
                />
              ) : (
                <div
                  className="form-control br-8 bg-light border d-flex align-items-center"
                  style={{ minHeight: 80, whiteSpace: "pre-wrap" }}
                >
                  {show(data.notes)}
                </div>
              )}
            </div>

            <div className="col-lg-6 mb-3">
              <label className="form-label">Status</label>
              {isEditMode ? (
                <select
                  className="form-control br-8"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <div className="form-control br-8 bg-light border d-flex align-items-center" style={{ height: 40 }}>
                  {show(data.status)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Action buttons - same as SignageProfile: Back left, Update right in edit */}
      <div className="d-flex gap-3 justify-content-between mb-4 mb-lg-0 mt-4">
        <button
          type="button"
          onClick={() => navigate("/superadmin/contact-requests")}
          className="btn back-btn surface-btn fw-bold fs-16"
        >
          Back
        </button>
        {isEditMode && (
          <button
            type="button"
            onClick={handleUpdate}
            disabled={saving}
            className="btn btn-primary save-btn surface-btn fw-bold fs-16 removal-installation"
          >
            {saving ? "Saving..." : "Update"}
          </button>
        )}
      </div>

      <style>{`
        input[type="text"], input[type="email"], input[type="number"], select.form-control, textarea.form-control {
          width: 100%;
          height: 40px;
          border-radius: 10px;
          padding: 10px 12px;
          border-width: 2px;
          border-style: solid;
          border-color: rgb(201, 206, 214);
          background-color: #f2f2f2a8 !important;
          outline: none;
          transition: 0.2s ease-in-out;
          box-shadow: none;
          color: rgb(17, 24, 39);
        }
        textarea.form-control { height: auto; min-height: 100px; }
        select.form-control.br-8 {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          padding-right: 36px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.13l3.71-2.9a.75.75 0 1 1 .92 1.19l-4.17 3.26a.75.75 0 0 1-.92 0L5.21 8.42a.75.75 0 0 1 .02-1.21z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
        }
      `}</style>
    </div>
  );
}
